import { useCallback, useMemo } from "react";
import { addMonths, format, subMonths } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { formatRecurrenceSummary } from "@/lib/recurrence";
import { buildStayFutureRows, type UpcomingStayDayRow } from "@/lib/daycare-hotel/stayFutureRows";
import type { AppointmentWithDetails } from "@/hooks/useAppointments";
import { useMedicalRecords } from "@/hooks/useMedicalRecords";
import { usePetVaccinations } from "@/hooks/usePetApplications";
import { usePetApplicationPlans, type PetApplicationPlan } from "@/hooks/usePetApplicationPlans";
import { usePet } from "@/hooks/usePets";
import { useClinicWhatsAppApplicationReminderRule } from "@/hooks/useClinicWhatsAppApplicationReminderRule";
import { useWhatsAppDoseState } from "@/hooks/useWhatsAppDoseState";
import { getReminderDeliveryStatus } from "@/lib/applications/reminder-schedule";
import { supabase } from "@/integrations/supabase/client";
import { formatDateBR, parseISODateOrDateTime, toISODateOnlyLocal } from "@/lib/date-utils";
import { computeReminderTriggerAt } from "@/lib/applications/reminder-schedule";
import { classifyUpcomingVaccinationRows } from "@/lib/applications/future-utils";
import { APPOINTMENT_STATUS_LABEL } from "@/components/patients/appointment-ui-meta";
import { shouldBlockRemindersForPet } from "@/lib/utils/pet-death";
import type { Database } from "@/integrations/supabase/types";

const ISO_DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const supabaseAny = supabase as unknown as {
  from: typeof supabase.from;
};

type MedicalRecordRow = Database["public"]["Tables"]["medical_records"]["Row"];
type ApplicationFollowUpTrackingRow = {
  pet_application_id?: string | null;
  pet_application_plan_id?: string | null;
  due_date: string;
  operational_status?: string | null;
  last_contact_at?: string | null;
};

const formatDateTimeBR = (value: string) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "-";
  return format(parsed, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
};

const formatDateOnlyBR = (value: string) => {
  const parsed = new Date(`${value}T12:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) return "-";
  return format(parsed, "dd/MM/yyyy", { locale: ptBR });
};

const formatDateFlexibleBR = (value?: string | null) => {
  if (!value) return "-";
  if (ISO_DATE_ONLY_REGEX.test(value)) return formatDateOnlyBR(value);
  return formatDateTimeBR(value);
};

const resolveUpcomingSortTimestamp = (fallbackDate: string) => {
  const fallbackTs = new Date(fallbackDate).getTime();
  return Number.isNaN(fallbackTs) ? 0 : fallbackTs;
};

/** Data prevista no calendário local (YYYY-MM-DD), para filtrar a aba "upcoming". */
function getExpectedDateKeyForFutureRow(row: FutureRow): string {
  if (row.editablePlan?.due_date) {
    const raw = row.editablePlan.due_date;
    if (ISO_DATE_ONLY_REGEX.test(raw.slice(0, 10))) return raw.slice(0, 10);
    const parsed = parseISODateOrDateTime(raw);
    return parsed ? toISODateOnlyLocal(parsed) : toISODateOnlyLocal(new Date(raw));
  }
  if (row.category === "vacina" && row.editableVaccination?.next_dose_date) {
    const raw = row.editableVaccination.next_dose_date;
    if (typeof raw !== "string") return toISODateOnlyLocal(new Date(row.timestamp));
    if (ISO_DATE_ONLY_REGEX.test(raw)) return raw;
    const parsed = parseISODateOrDateTime(raw);
    return parsed ? toISODateOnlyLocal(parsed) : toISODateOnlyLocal(new Date(raw));
  }
  return toISODateOnlyLocal(new Date(row.timestamp));
}

const findMatchingReturnAppointmentRow = ({
  petId,
  followUpDateKey,
  appointmentRows,
}: {
  petId?: string | null;
  followUpDateKey: string;
  appointmentRows: FutureRow[];
}) => {
  if (!petId) return null;

  return appointmentRows.find((row) => {
    if (row.category !== "agendamento" || row.typeLabel !== "Retorno") return false;
    if (row.petId !== petId || !row.appointmentId) return false;
    return toISODateOnlyLocal(new Date(row.timestamp)) === followUpDateKey;
  }) ?? null;
};

function extractUpcomingFollowUpsFromRecords(
  medicalRecords: Array<{ id: string; follow_up_date?: string | null }>,
): Array<{ timestamp: number; label: string; dateKey: string }> {
  const nowTs = Date.now();
  const todayIso = new Date().toISOString().slice(0, 10);
  return medicalRecords
    .map((record) => {
      const followUpRaw = record.follow_up_date;
      if (!followUpRaw || typeof followUpRaw !== "string") return null;
      if (ISO_DATE_ONLY_REGEX.test(followUpRaw)) {
        if (followUpRaw < todayIso) return null;
        const safe = new Date(`${followUpRaw}T12:00:00.000Z`);
        if (Number.isNaN(safe.getTime())) return null;
        return { timestamp: safe.getTime(), label: formatDateBR(followUpRaw), dateKey: followUpRaw };
      }
      const parsed = new Date(followUpRaw);
      if (Number.isNaN(parsed.getTime()) || parsed.getTime() <= nowTs) return null;
      return {
        timestamp: parsed.getTime(),
        label: formatDateBR(followUpRaw),
        dateKey: toISODateOnlyLocal(parsed),
      };
    })
    .filter(Boolean)
    .slice(0, 5) as Array<{ timestamp: number; label: string; dateKey: string }>;
}

async function fetchPetApplicationsForOwnerScope(petId: string) {
  const { data, error } = await supabaseAny
    .from("pet_applications")
    .select(`
      *,
      clinics(id, name),
      item:items!item_id(id, name, description, sale_price, product_subtype:product_subtypes(name)),
      next_application_item:items!next_application_item_id(id, name, description, sale_price, product_subtype:product_subtypes(name)),
      appointment:appointments(id, starts_at, type, status),
      medical_record:medical_records(id, consultation_date)
    `)
    .eq("pet_id", petId)
    .is("deleted_at", null)
    .order("application_date", { ascending: false });

  if (error) throw error;
  return data ?? [];
}

async function fetchPetApplicationPlansForOwnerScope(petId: string): Promise<PetApplicationPlan[]> {
  const statuses = ["pending", "scheduled"] as const;
  const query = supabaseAny
    .from("pet_application_plans")
    .select(`
      *,
      item:items!item_id(id, name, description, sale_price, product_subtype:product_subtypes(name)),
      pet:pets!pet_id(id, name),
      appointment:appointments(id, starts_at, status, type)
    `)
    .eq("pet_id", petId)
    .is("deleted_at", null)
    .in("status", [...statuses]);

  const { data, error } = await query.order("due_date", { ascending: true });
  if (error) throw error;
  return (data ?? []) as PetApplicationPlan[];
}

async function fetchMedicalRecordsForOwnerScope(petId: string): Promise<MedicalRecordRow[]> {
  const query = supabase
    .from("medical_records")
    .select(`
      *,
      pets (
        id,
        name,
        owners (
          id,
          name
        )
      ),
      profiles!fk_vet_user (
        id,
        full_name
      ),
      clinics (
        id,
        name
      )
    `)
    .is("deleted_at", null)
    .order("consultation_date", { ascending: false })
    .eq("pet_id", petId);

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as MedicalRecordRow[];
}

async function fetchApplicationFollowUpTrackingState({
  clinicId,
  startDate,
  endDate,
}: {
  clinicId: string;
  startDate: string;
  endDate: string;
}): Promise<Map<string, { operationalStatus: string | null; lastContactAt: string | null }>> {
  const { data, error } = await supabaseAny
    .from("application_follow_up_tracking")
    .select("pet_application_id, pet_application_plan_id, due_date, operational_status, last_contact_at")
    .eq("clinic_id", clinicId)
    .gte("due_date", startDate)
    .lte("due_date", endDate);

  if (error) throw error;

  const map = new Map<string, { operationalStatus: string | null; lastContactAt: string | null }>();
  for (const row of (data ?? []) as ApplicationFollowUpTrackingRow[]) {
    const dueDateKey = row.due_date?.slice(0, 10);
    if (!dueDateKey) continue;

    if (row.pet_application_id) {
      map.set(`app:${row.pet_application_id}:${dueDateKey}`, {
        operationalStatus: row.operational_status ?? null,
        lastContactAt: row.last_contact_at ?? null,
      });
    }

    if (row.pet_application_plan_id) {
      map.set(`plan:${row.pet_application_plan_id}:${dueDateKey}`, {
        operationalStatus: row.operational_status ?? null,
        lastContactAt: row.last_contact_at ?? null,
      });
    }
  }

  return map;
}

export interface FutureRow {
  key: string;
  category: "vacina" | "retorno" | "agendamento" | "estadia";
  typeLabel: string;
  detailLabel?: string | null;
  timestamp: number;
  dateLabel: string;
  appointmentDateLabel?: string;
  statusLabel: string;
  statusKey?: string | null;
  isScheduled: boolean;
  appointmentId?: string;
  estimatedAmount: number | null;
  resolutionStatus: "scheduled" | "pending" | "resolved_applied";
  petId?: string | null;
  petName?: string | null;
  ownerId?: string | null;
  ownerName?: string | null;
  professionalName?: string | null;
  tutorReminderStatus?: "notified" | "not_notified" | "window_passed" | null;
  tutorReminderDateLabel?: string | null;
  editablePlan?: {
    id: string;
    item_id: string;
    due_date: string;
    notes?: string | null;
    appointment_id?: string | null;
    source_type?: "manual" | "derived_from_application" | null;
    source_pet_application_id?: string | null;
    appointment?: { id?: string; starts_at?: string | null; status?: string | null; type?: string | null } | null;
  } | null;
  editableVaccination?: {
    dose_number?: number | null;
    notes?: string | null;
    id?: string;
    item_id?: string | { id?: string; name?: string | null } | null;
    next_application_item_id?: string | { id?: string; name?: string | null } | null;
    item?: { name?: string | null } | null;
    next_application_item?: { name?: string | null } | null;
    application_date: string | Date;
    expiry_date?: string | null;
    next_dose_date?: string | null;
    has_next_dose?: boolean | null;
    next_dose_completed_at?: string | null;
    clinic_id?: string | null;
    appointment_id?: string | null;
    appointment?: { starts_at?: string | null } | null;
    medical_record_id?: string | null;
  } | null;
}

export interface CareSections {
  pendingRows: FutureRow[];
  upcomingRows: FutureRow[];
}

export type OwnerPetSummary = {
  id: string;
  name: string;
  status?: { name?: string | null } | null;
};

type Scope =
  | {
    scope: "patient";
    petId: string;
    ownerId?: string | null;
    clinicId?: string | null;
    includeGrooming?: boolean;
    includeLinkedAppointmentRows?: boolean;
  }
  | {
    scope: "owner";
    ownerId: string;
    petIds: string[];
    petSummaries?: OwnerPetSummary[];
    clinicId?: string | null;
    includeGrooming?: boolean;
    includeLinkedAppointmentRows?: boolean;
  };

type PetVaccinationUpcoming = {
  id: string;
  pet_id?: string | null;
  application_date?: string | null;
  next_dose_date: string | null;
  has_next_dose?: boolean | null;
  next_dose_completed_at?: string | null;
  item_id?: string | { id?: string | null; name?: string | null; sale_price?: number | null } | null;
  next_application_item?: { id?: string | null; name?: string | null; sale_price?: number | null } | null;
  appointment?: { id: string; status: string | null; starts_at: string; type: string | null } | null;
  resolvedByVaccinationId?: string;
  resolvedAppliedAt?: string;
  resolutionStatus?: "scheduled" | "pending" | "resolved_applied";
  isDateOnly?: boolean;
  nextDoseRaw?: string;
  nextDoseTimestamp?: number;
};

type ClassifiedVaccinationRow = {
  vaccineName: string;
  vaccinationId: string;
  itemId?: string | null;
  estimatedAmount?: number | null;
  nextDoseTimestamp: number;
  nextDoseRaw: string;
  isDateOnly?: boolean;
  resolutionStatus: "scheduled" | "pending";
  appointment?: { id: string; status: string | null; starts_at: string } | null;
};

const PLAN_STATUSES_KEY = ["pending", "scheduled"].join(",");

async function fetchFutureRowsAppointmentsByPetIds({
  petIds,
  start,
  end,
  includeGrooming,
}: {
  petIds: string[];
  start: Date;
  end: Date;
  includeGrooming: boolean;
}): Promise<AppointmentWithDetails[]> {
  if (petIds.length === 0) return [];

  const query = supabaseAny
    .from("appointments")
    .select(`
      id,
      clinic_id,
      vet_user_id,
      owner_id,
      pet_id,
      type,
      status,
      starts_at,
      ends_at,
      notes,
      created_by_user_id,
      created_at,
      updated_at,
      clinic_appointment_type:clinic_appointment_types(id, name, color, icon, behavior),
      pet:pets!appointments_pet_id_fkey(id, name),
      owner:owners!inner(id, name, deleted_at),
      vet:profiles!appointments_vet_user_id_fkey(id, full_name),
      appointment_items(
        id,
        appointment_id,
        item_id,
        qty,
        title_snapshot,
        item:items(id, name, type, sale_price)
      )
    `)
    .in("pet_id", petIds)
    .gte("starts_at", start.toISOString())
    .lte("starts_at", end.toISOString())
    .is("deleted_at", null)
    .is("owner.deleted_at", null)
    .order("starts_at", { ascending: true });

  const { data, error } = await query;
  if (error) throw error;

  return ((data ?? []) as AppointmentWithDetails[]).filter((appointment) => {
    if (!includeGrooming && appointment.clinic_appointment_type?.behavior === "banho_tosa") return false;
    return true;
  });
}

/**
 * De onde sai o rótulo de repetição de uma diária futura.
 *
 * A reserva da matrícula não tem mais `stay_reservation_recurrence` — os dias
 * fixos vivem no contrato — então ela precisa de uma fonte própria.
 */
export type StayFutureRecurrenceSource =
  | { kind: "plan"; count: number }
  | { kind: "rule"; frequency: "weekly" | "monthly"; interval: number; endsOn: string };

export function describeStayFutureRecurrence({
  rule,
  plan,
}: {
  rule: { frequency: string; intervalDays: number | null; endsOn: string | null } | null;
  plan: { fixedDays: number; usageLimit: number | null } | null;
}): StayFutureRecurrenceSource | null {
  if (plan && plan.fixedDays > 0) {
    return { kind: "plan", count: plan.usageLimit ?? plan.fixedDays };
  }
  if (!rule?.endsOn) return null;
  return {
    kind: "rule",
    frequency: rule.frequency === "monthly" ? "monthly" : "weekly",
    interval: rule.intervalDays === 14 ? 2 : 1,
    endsOn: rule.endsOn,
  };
}

export function useFutureRows(params: Scope) {
  const { t } = useTranslation("patients");
  const now = useMemo(() => new Date(), []);
  const start = useMemo(() => subMonths(now, 6), [now]);
  const end = useMemo(() => addMonths(now, 6), [now]);
  const includeGrooming = params.includeGrooming ?? true;
  const includeLinkedAppointmentRows = params.includeLinkedAppointmentRows ?? true;
  const patientPetId = params.scope === "patient" ? params.petId : null;
  const ownerPetIds = params.scope === "owner" ? params.petIds : undefined;
  const petIds = useMemo(
    () => (params.scope === "patient" ? [patientPetId] : ownerPetIds ?? []),
    [ownerPetIds, params.scope, patientPetId],
  );
  const resolvedPetIds = useMemo(() => petIds.filter((id): id is string => Boolean(id)), [petIds]);

  const stayDaysQuery = useQuery({
    queryKey: ["future-stay-days", resolvedPetIds],
    enabled: resolvedPetIds.length > 0,
    queryFn: async (): Promise<UpcomingStayDayRow[]> => {
      const today = new Date().toLocaleDateString("en-CA");
      const { data, error } = await (supabase as any) // eslint-disable-line @typescript-eslint/no-explicit-any
        .from("stay_reservation_days")
        .select(
          `stay_date, pet_id, stay_reservation_id,
           stay_reservations!inner ( module_type,
             stay_reservation_recurrence ( frequency, days_of_week, interval_days, ends_on ),
             plan_contract:daycare_plan_contracts ( terms_snapshot, contract_days:daycare_plan_contract_days ( weekday ) ) ),
           pets ( name )`,
        )
        .in("pet_id", resolvedPetIds)
        .eq("status", "scheduled")
        .gte("stay_date", today)
        .order("stay_date", { ascending: true });
      if (error) throw error;
      return ((data ?? []) as Array<{
        stay_date: string;
        pet_id: string;
        stay_reservation_id: string;
        stay_reservations: {
          module_type: string;
          stay_reservation_recurrence: Array<{ frequency: string; days_of_week: number[] | null; interval_days: number | null; ends_on: string | null }> | null;
          plan_contract: {
            terms_snapshot: { usage_limit?: number | null } | null;
            contract_days: Array<{ weekday: number }> | null;
          } | null;
        } | null;
        pets: { name: string | null } | null;
      }>).map((row) => {
        const reservation = row.stay_reservations;
        const rule = reservation?.stay_reservation_recurrence?.[0] ?? null;
        const source = describeStayFutureRecurrence({
          rule: rule ? { frequency: rule.frequency, intervalDays: rule.interval_days, endsOn: rule.ends_on } : null,
          plan: reservation?.plan_contract
            ? {
                fixedDays: (reservation.plan_contract.contract_days ?? []).length,
                usageLimit: reservation.plan_contract.terms_snapshot?.usage_limit ?? null,
              }
            : null,
        });
        return {
          reservationId: row.stay_reservation_id,
          petId: row.pet_id,
          petName: row.pets?.name ?? null,
          stayDate: row.stay_date,
          moduleType: reservation?.module_type ?? "daycare",
          recurrenceSummary:
            source?.kind === "plan"
              ? t("futureRows.planRecurrence", { ns: "daycareHotel", count: source.count })
              : source?.kind === "rule"
                ? formatRecurrenceSummary({
                    frequency: source.frequency,
                    interval: source.interval,
                    endsOn: source.endsOn,
                  })
                : null,
        };
      });
    },
  });

  const stayFutureRows = useMemo(
    () => buildStayFutureRows(stayDaysQuery.data ?? [], t),
    [stayDaysQuery.data, t],
  );
  const primaryPetId = params.scope === "patient" ? params.petId : null;
  const { data: primaryPet, isLoading: isLoadingPet } = usePet(primaryPetId ?? "");
  const shouldSuppressPatientAutomations =
    params.scope === "patient" && shouldBlockRemindersForPet(primaryPet);

  const ownerPetIdsKey = params.scope === "owner" ? params.petIds.join("\u0000") : "";
  const ownerPetIdsForQueries = useMemo(
    () => {
      if (params.scope !== "owner") return [];
      return params.petIds;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ownerPetIdsKey tracks params.petIds when scope is owner
    [params.scope, ownerPetIdsKey],
  );

  const ownerVaccinationQueries = useQueries({
    queries: ownerPetIdsForQueries.map((petId) => ({
      queryKey: ["pet-applications", petId] as const,
      queryFn: () => fetchPetApplicationsForOwnerScope(petId),
      enabled: params.scope === "owner" && Boolean(petId),
    })),
  });

  const ownerPlanQueries = useQueries({
    queries: ownerPetIdsForQueries.map((petId) => ({
      queryKey: ["pet-application-plans", petId, PLAN_STATUSES_KEY] as const,
      queryFn: () => fetchPetApplicationPlansForOwnerScope(petId),
      enabled: params.scope === "owner" && Boolean(petId),
    })),
  });

  const ownerMedicalQueries = useQueries({
    queries: ownerPetIdsForQueries.map((petId) => ({
      queryKey: ["medical-records", petId, undefined] as const,
      queryFn: () => fetchMedicalRecordsForOwnerScope(petId),
      enabled: params.scope === "owner" && Boolean(petId),
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
    })),
  });

  const { data: appointments = [], isLoading: isLoadingAppointments } = useQuery({
    queryKey: ["future-rows-appointments", petIds, start.toISOString(), end.toISOString(), includeGrooming],
    queryFn: () => fetchFutureRowsAppointmentsByPetIds({
      petIds,
      start,
      end,
      includeGrooming,
    }),
    enabled: petIds.length > 0,
  });

  const trackingStartDate = useMemo(() => toISODateOnlyLocal(start), [start]);
  const trackingEndDate = useMemo(() => toISODateOnlyLocal(end), [end]);

  const { data: followUpTrackingMap } = useQuery({
    queryKey: ["application-follow-up-tracking", params.clinicId, trackingStartDate, trackingEndDate],
    queryFn: () => fetchApplicationFollowUpTrackingState({
      clinicId: params.clinicId!,
      startDate: trackingStartDate,
      endDate: trackingEndDate,
    }),
    enabled: Boolean(params.clinicId),
  });

  const { medicalRecords = [], isLoading: isLoadingMedicalRecords } = useMedicalRecords(primaryPetId ?? "");
  const { data: vaccinations = [], isLoading: isLoadingVaccinations } = usePetVaccinations(primaryPetId ?? "");
  const { data: applicationPlans = [], isLoading: isLoadingApplicationPlans } = usePetApplicationPlans(primaryPetId ?? "", {
    statuses: ["pending", "scheduled"],
  });
  const { data: reminderRule } = useClinicWhatsAppApplicationReminderRule(params.clinicId ?? null);
  const { data: doseStateMap } = useWhatsAppDoseState(params.clinicId ? [params.clinicId] : []);

  const getAppointmentEstimatedAmount = (appointment: AppointmentWithDetails) =>
    (appointment.appointment_items ?? []).reduce(
      (sum, item) => sum + Number(item.qty ?? 1) * Number(item.item?.sale_price ?? 0),
      0,
    );
  const getProfessionalName = (appointment?: AppointmentWithDetails) =>
    (appointment?.vet as { full_name?: string | null } | null)?.full_name ?? null;
  const getReminderDateLabel = useCallback((dueDate: string) => {
    if (!reminderRule?.is_active) return null;
    try {
      return formatDateTimeBR(
        computeReminderTriggerAt(dueDate.slice(0, 10), {
          offsetUnit: reminderRule.offset_unit,
          offsetValue: reminderRule.offset_value,
          sendTime: reminderRule.send_time,
          timezone: reminderRule.timezone,
        }).sendAt.toISOString(),
      );
    } catch {
      return null;
    }
  }, [reminderRule]);

  const upcomingAppointments = useMemo(() => {
    const excludedStatuses = new Set(["cancelado", "faltou", "finalizado", "pago"]);
    const nowTs = Date.now();

    return (appointments ?? [])
      .filter((appointment: AppointmentWithDetails) => {
        if (!appointment?.id) return false;
        if (!includeGrooming && appointment.clinic_appointment_type?.behavior === "banho_tosa") return false;
        if (excludedStatuses.has(appointment.status)) return false;
        const startsAt = new Date(appointment.starts_at).getTime();
        if (Number.isNaN(startsAt) || startsAt <= nowTs) return false;
        const ids = new Set([
          appointment.pet_id,
          appointment.pet?.id,
          ...(appointment.pets ?? []).map((pet) => pet?.id),
        ].filter(Boolean));
        return petIds.some((id) => ids.has(id));
      })
      .sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime());
  }, [appointments, includeGrooming, petIds]);

  const vaccineAppointmentPool = useMemo(
    () =>
      upcomingAppointments
        .filter((appointment) => new Set(["vacina", "consulta", "retorno", "aplicacao"]).has(appointment.clinic_appointment_type?.behavior ?? ""))
        .map((appointment) => ({
          id: appointment.id,
          type: appointment.clinic_appointment_type?.behavior,
          starts_at: appointment.starts_at,
          status: appointment.status,
          estimatedAmount: getAppointmentEstimatedAmount(appointment),
        })),
    [upcomingAppointments],
  );

  const classifiedVaccinations = useMemo(() => {
    if (params.scope !== "patient") return [];
    return classifyUpcomingVaccinationRows({
      vaccinations: vaccinations as unknown as PetVaccinationUpcoming[],
      appointments: vaccineAppointmentPool,
      includePast: true,
      getVaccineName: (vaccination) => {
        if ((vaccination as { next_application_item?: { name?: string | null } | null }).next_application_item?.name) {
          return (vaccination as { next_application_item?: { name?: string | null } | null }).next_application_item?.name ?? "Aplicação";
        }
        if (vaccination.item_id && typeof vaccination.item_id === "object" && vaccination.item_id.name) {
          return vaccination.item_id.name;
        }
        return "Aplicação";
      },
    });
  }, [params.scope, vaccinations, vaccineAppointmentPool]) as ClassifiedVaccinationRow[];

  const classifiedVaccinationsWithPrice = useMemo(
    () =>
      classifiedVaccinations.map((vacc) => {
        const source = (vaccinations as PetVaccinationUpcoming[]).find((item) => item.id === vacc.vaccinationId);
        const estimatedAmount =
          source?.next_application_item?.sale_price
          ?? (source?.item_id && typeof source.item_id === "object" ? source.item_id.sale_price ?? null : null);
        return {
          ...vacc,
          estimatedAmount,
        };
      }),
    [classifiedVaccinations, vaccinations],
  );

  const upcomingFollowUps = useMemo(() => {
    if (params.scope !== "patient") return [];
    return extractUpcomingFollowUpsFromRecords(
      medicalRecords as Array<{ id: string; follow_up_date?: string | null }>,
    );
  }, [params.scope, medicalRecords]);

  const rows = useMemo<FutureRow[]>(() => {
    const appointmentRows: FutureRow[] = upcomingAppointments.map((appointment) => {
      const typeLabel = (appointment as { clinic_appointment_type?: { name?: string | null } | null }).clinic_appointment_type?.name ?? appointment.type ?? "";
      const itemDetail = appointment.appointment_items?.[0]?.item?.name?.trim()
        || appointment.appointment_items?.[0]?.title_snapshot?.trim()
        || null;
      const petName =
        appointment.pet?.id && petIds.includes(appointment.pet.id as string)
          ? (appointment.pet.name as string | undefined) ?? null
          : (appointment.pets ?? []).find((pet) => pet?.id && petIds.includes(pet.id as string))?.name as string | undefined ?? (appointment.pet?.name as string | undefined) ?? null;
      return {
        key: `appointment-${appointment.id}`,
        category: "agendamento",
        typeLabel,
        detailLabel: appointment.clinic_appointment_type?.behavior === "vacina" || appointment.clinic_appointment_type?.behavior === "aplicacao" ? itemDetail : null,
        timestamp: new Date(appointment.starts_at).getTime(),
        dateLabel: formatDateFlexibleBR(appointment.starts_at),
        appointmentDateLabel: formatDateTimeBR(appointment.starts_at),
        statusLabel: APPOINTMENT_STATUS_LABEL[appointment.status] ?? appointment.status,
        statusKey: appointment.status,
        isScheduled: true,
        appointmentId: appointment.id,
        estimatedAmount: getAppointmentEstimatedAmount(appointment),
        resolutionStatus: "scheduled",
        petId: (appointment.pet?.id as string | undefined) ?? appointment.pet_id ?? null,
        petName,
        ownerId: (appointment.owner?.id as string | undefined) ?? null,
        ownerName: (appointment.owner?.name as string | undefined) ?? null,
        professionalName: getProfessionalName(appointment),
        tutorReminderStatus: null,
        tutorReminderDateLabel: null,
        editablePlan: null,
        editableVaccination: null,
      };
    });

    if (params.scope === "owner") {
      const petSummaries = params.petSummaries ?? [];
      const resolvePetName = (pid: string) => petSummaries.find((p) => p.id === pid)?.name ?? null;

      const allPlans = ownerPetIdsForQueries.flatMap((_, i) => ownerPlanQueries[i]?.data ?? []);
      const plannedKeys = new Set(
        allPlans.map((plan) => `${plan.item_id}:${plan.due_date.slice(0, 10)}`),
      );
      const plannedSourceVaccinationIds = new Set(
        allPlans
          .map((plan) => plan.source_pet_application_id)
          .filter((sourceId): sourceId is string => Boolean(sourceId)),
      );

      const planRowsAll: FutureRow[] = [];
      const vaccineRowsAll: FutureRow[] = [];
      const returnRowsAll: FutureRow[] = [];

      for (let i = 0; i < ownerPetIdsForQueries.length; i++) {
        const petId = ownerPetIdsForQueries[i];
        const petName = resolvePetName(petId);
        const shouldSuppress = shouldBlockRemindersForPet(petSummaries.find((p) => p.id === petId));
        const vaccinationsForPet = (ownerVaccinationQueries[i]?.data ?? []) as unknown as PetVaccinationUpcoming[];

        const classified = classifyUpcomingVaccinationRows({
          vaccinations: vaccinationsForPet,
          appointments: vaccineAppointmentPool,
          includePast: true,
          getVaccineName: (vaccination) => {
            if ((vaccination as { next_application_item?: { name?: string | null } | null }).next_application_item?.name) {
              return (vaccination as { next_application_item?: { name?: string | null } | null }).next_application_item?.name ?? "Aplicação";
            }
            if (vaccination.item_id && typeof vaccination.item_id === "object" && vaccination.item_id.name) {
              return vaccination.item_id.name;
            }
            return "Aplicação";
          },
        });

        const classifiedWithPrice = classified.map((vacc) => {
          const source = vaccinationsForPet.find((item) => item.id === vacc.vaccinationId);
          const estimatedAmount =
            source?.next_application_item?.sale_price
            ?? (source?.item_id && typeof source.item_id === "object" ? source.item_id.sale_price ?? null : null);
          return {
            ...vacc,
            estimatedAmount,
          };
        });

        const plansForPet = ownerPlanQueries[i]?.data ?? [];
        const planRows: FutureRow[] = (shouldSuppress ? [] : plansForPet).map((plan) => {
          const isScheduled = plan.status === "scheduled";
          const dueDateKey = plan.due_date.slice(0, 10);
          const trackingState = followUpTrackingMap?.get(`plan:${plan.id}:${dueDateKey}`);
          const reminderKey = plan.source_pet_application_id ? `${plan.source_pet_application_id}:${dueDateKey}` : null;
          const reminderState = reminderKey ? doseStateMap?.get(reminderKey) : undefined;
          const alreadyNotified = Boolean(trackingState?.lastContactAt || reminderState?.is_already_sent);
          const reminderSentDateLabel = reminderState?.is_already_sent
            ? formatDateFlexibleBR(reminderState.latest_created_at)
            : null;
          const trackingSentDateLabel = trackingState?.lastContactAt
            ? formatDateFlexibleBR(trackingState.lastContactAt)
            : null;
          const deliveryStatus = alreadyNotified ? null : getReminderDeliveryStatus({
            dueDate: plan.due_date,
            createdAt: (plan as { created_at?: string | null }).created_at ?? null,
            lastContactAt: null,
            rule: reminderRule ? {
              isActive: reminderRule.is_active,
              offsetUnit: reminderRule.offset_unit,
              offsetValue: reminderRule.offset_value,
              sendTime: reminderRule.send_time,
              timezone: reminderRule.timezone,
            } : null,
          });
          const reminderDateLabel = deliveryStatus?.type === "scheduled" || deliveryStatus?.type === "expired"
            ? formatDateTimeBR((deliveryStatus as { scheduledAt: Date }).scheduledAt.toISOString())
            : null;
          const planTutorReminderStatus: FutureRow["tutorReminderStatus"] =
            alreadyNotified ? "notified"
            : deliveryStatus?.type === "expired" ? "window_passed"
            : reminderDateLabel ? "not_notified"
            : null;
          return {
            key: `plan-${plan.id}`,
            category: "vacina" as const,
            typeLabel: "Vacina",
            detailLabel: plan.item?.name?.trim() || plan.item?.product_subtype?.name?.trim() || null,
            timestamp: resolveUpcomingSortTimestamp(plan.due_date),
            dateLabel: formatDateFlexibleBR(plan.due_date),
            appointmentDateLabel: isScheduled && plan.appointment?.starts_at ? formatDateTimeBR(plan.appointment.starts_at) : undefined,
            statusLabel: isScheduled ? (APPOINTMENT_STATUS_LABEL[plan.appointment?.status ?? ""] ?? "Agendado") : "Sem agendamento",
            statusKey: isScheduled ? plan.appointment?.status ?? null : null,
            isScheduled,
            appointmentId: isScheduled ? plan.appointment?.id ?? undefined : undefined,
            estimatedAmount: plan.item?.sale_price ?? null,
            resolutionStatus: isScheduled ? "scheduled" as const : "pending" as const,
            petId,
            petName: petName ?? plan.pet?.name ?? null,
            ownerId: params.ownerId,
            ownerName: null,
            professionalName: isScheduled ? getProfessionalName(upcomingAppointments.find((appointment) => appointment.id === plan.appointment?.id)) : null,
            tutorReminderStatus: planTutorReminderStatus,
            tutorReminderDateLabel: trackingState?.lastContactAt ? trackingSentDateLabel : reminderState?.is_already_sent ? reminderSentDateLabel : reminderDateLabel,
            editablePlan: {
              id: plan.id,
              item_id: plan.item_id,
              due_date: plan.due_date,
              notes: plan.notes,
              appointment_id: plan.appointment_id,
              source_type: plan.source_type,
              source_pet_application_id: plan.source_pet_application_id,
              appointment: plan.appointment ?? null,
            },
            editableVaccination: null,
          };
        });
        planRowsAll.push(...planRows);

      const vaccineRows: FutureRow[] = (shouldSuppress ? [] : classifiedWithPrice)
        .filter((vacc) => {
          if (plannedSourceVaccinationIds.has(vacc.vaccinationId)) return false;
          return !plannedKeys.has(`${vacc.itemId ?? ""}:${vacc.nextDoseRaw.slice(0, 10)}`);
        })
        .map((vacc) => {
          const isScheduled = vacc.resolutionStatus === "scheduled";
          const appointmentStatus = vacc.appointment?.status ?? "";
          const dueDateKey = vacc.nextDoseRaw.slice(0, 10);
          const trackingState = followUpTrackingMap?.get(`app:${vacc.vaccinationId}:${dueDateKey}`);
          const reminderKey = `${vacc.vaccinationId}:${dueDateKey}`;
          const reminderState = doseStateMap?.get(reminderKey);
          const matchedAppointment = vacc.appointment?.id
            ? upcomingAppointments.find((appointment) => appointment.id === vacc.appointment?.id)
            : undefined;
          const alreadyNotifiedVacc = Boolean(trackingState?.lastContactAt || reminderState?.is_already_sent);
          const reminderSentDateLabel = reminderState?.is_already_sent
            ? formatDateFlexibleBR(reminderState.latest_created_at)
            : null;
          const trackingSentDateLabel = trackingState?.lastContactAt
            ? formatDateFlexibleBR(trackingState.lastContactAt)
            : null;
          const vaccDeliveryStatus = alreadyNotifiedVacc ? null : getReminderDeliveryStatus({
            dueDate: vacc.nextDoseRaw,
            createdAt: (vaccinationsForPet.find((item) => item.id === vacc.vaccinationId) as { created_at?: string | null } | undefined)?.created_at ?? null,
            lastContactAt: null,
            rule: reminderRule ? {
              isActive: reminderRule.is_active,
              offsetUnit: reminderRule.offset_unit,
              offsetValue: reminderRule.offset_value,
              sendTime: reminderRule.send_time,
              timezone: reminderRule.timezone,
            } : null,
          });
          const vaccReminderDateLabel = vaccDeliveryStatus?.type === "scheduled" || vaccDeliveryStatus?.type === "expired"
            ? formatDateTimeBR((vaccDeliveryStatus as { scheduledAt: Date }).scheduledAt.toISOString())
            : null;
          const vaccTutorReminderStatus: FutureRow["tutorReminderStatus"] =
            alreadyNotifiedVacc ? "notified"
            : vaccDeliveryStatus?.type === "expired" ? "window_passed"
            : vaccReminderDateLabel ? "not_notified"
            : null;
          return {
              key: `vaccine-${petId}-${vacc.vaccinationId}`,
              category: "vacina" as const,
              typeLabel: "Vacina",
              detailLabel: vacc.vaccineName,
              timestamp: resolveUpcomingSortTimestamp(vacc.nextDoseRaw),
              dateLabel: vacc.isDateOnly ? formatDateOnlyBR(vacc.nextDoseRaw) : formatDateTimeBR(vacc.nextDoseRaw),
              appointmentDateLabel: isScheduled && vacc.appointment?.starts_at ? formatDateTimeBR(vacc.appointment.starts_at) : undefined,
              statusLabel: isScheduled ? (APPOINTMENT_STATUS_LABEL[appointmentStatus] ?? "Agendado") : "Sem agendamento",
              statusKey: isScheduled ? appointmentStatus : null,
              isScheduled,
              appointmentId: isScheduled ? vacc.appointment?.id ?? undefined : undefined,
              estimatedAmount: vacc.appointment?.id
                ? vaccineAppointmentPool.find((appointment) => appointment.id === vacc.appointment?.id)?.estimatedAmount ?? vacc.estimatedAmount ?? null
                : vacc.estimatedAmount ?? null,
              resolutionStatus: vacc.resolutionStatus,
              petId,
              petName,
              ownerId: params.ownerId,
              ownerName: null,
              professionalName: getProfessionalName(matchedAppointment),
              tutorReminderStatus: vaccTutorReminderStatus,
              tutorReminderDateLabel: trackingState?.lastContactAt ? trackingSentDateLabel : reminderState?.is_already_sent ? reminderSentDateLabel : vaccReminderDateLabel,
              editablePlan: null,
              editableVaccination: vaccinationsForPet.find((item) => item.id === vacc.vaccinationId) as FutureRow["editableVaccination"],
            };
          });
        vaccineRowsAll.push(...vaccineRows);

        const medicalRecordsForPet = (ownerMedicalQueries[i]?.data ?? []) as Array<{ id: string; follow_up_date?: string | null }>;
        const upcomingFollowUpsForPet = extractUpcomingFollowUpsFromRecords(medicalRecordsForPet);
        const returnRows: FutureRow[] = (shouldSuppress ? [] : upcomingFollowUpsForPet)
          .map((item, index) => {
            const matchingAppointment = findMatchingReturnAppointmentRow({
              petId,
              followUpDateKey: item.dateKey,
              appointmentRows,
            });

            return {
              key: `followup-${petId}-${index}`,
              category: "retorno" as const,
              typeLabel: "Retorno",
              timestamp: item.timestamp,
              dateLabel: item.label,
              appointmentDateLabel: matchingAppointment?.appointmentDateLabel,
              statusLabel: matchingAppointment?.statusLabel ?? "Sem agendamento",
              statusKey: matchingAppointment?.statusKey ?? null,
              isScheduled: Boolean(matchingAppointment?.appointmentId),
              appointmentId: matchingAppointment?.appointmentId,
              estimatedAmount: null,
              resolutionStatus: matchingAppointment?.appointmentId ? "scheduled" as const : "pending" as const,
              petId,
              petName,
              ownerId: params.ownerId,
              ownerName: null,
              professionalName: matchingAppointment?.professionalName ?? null,
              tutorReminderStatus: null,
              tutorReminderDateLabel: null,
              editablePlan: null,
              editableVaccination: null,
            };
          })
          .filter(Boolean) as FutureRow[];
        returnRowsAll.push(...returnRows);
      }

      const combinedRows = [
        ...planRowsAll,
        ...vaccineRowsAll,
        ...returnRowsAll,
        ...stayFutureRows,
        ...(
          includeLinkedAppointmentRows
            ? appointmentRows
            : (() => {
              const scheduledAppointmentIdsFromVaccines = new Set(vaccineRowsAll.flatMap((row) => (row.appointmentId ? [row.appointmentId] : [])));
              const scheduledAppointmentIdsFromPlans = new Set(planRowsAll.flatMap((row) => (row.appointmentId ? [row.appointmentId] : [])));
              const scheduledAppointmentIdsFromReturns = new Set(returnRowsAll.flatMap((row) => (row.appointmentId ? [row.appointmentId] : [])));

              return appointmentRows.filter((row) => {
                if (!row.appointmentId) return true;
                return !scheduledAppointmentIdsFromVaccines.has(row.appointmentId)
                  && !scheduledAppointmentIdsFromPlans.has(row.appointmentId)
                  && !scheduledAppointmentIdsFromReturns.has(row.appointmentId);
              });
            })()
        ),
      ].sort((a, b) => a.timestamp - b.timestamp);

      return combinedRows;
    }

    const plannedKeys = new Set(
      applicationPlans.map((plan) => `${plan.item_id}:${plan.due_date.slice(0, 10)}`),
    );
    const plannedSourceVaccinationIds = new Set(
      applicationPlans
        .map((plan) => plan.source_pet_application_id)
        .filter((sourceId): sourceId is string => Boolean(sourceId)),
    );
    const patientOwnerId = params.ownerId ?? primaryPet?.owner?.id ?? null;
    const patientOwnerName = primaryPet?.owner?.name ?? null;

    const planRows: FutureRow[] = (shouldSuppressPatientAutomations ? [] : applicationPlans).map((plan) => {
      const isScheduled = plan.status === "scheduled";
      const dueDateKey = plan.due_date.slice(0, 10);
      const trackingState = followUpTrackingMap?.get(`plan:${plan.id}:${dueDateKey}`);
      const reminderKey = plan.source_pet_application_id ? `${plan.source_pet_application_id}:${dueDateKey}` : null;
      const reminderState = reminderKey ? doseStateMap?.get(reminderKey) : undefined;
      const alreadyNotifiedPlan = Boolean(trackingState?.lastContactAt || reminderState?.is_already_sent);
      const reminderSentDateLabel = reminderState?.is_already_sent
        ? formatDateFlexibleBR(reminderState.latest_created_at)
        : null;
      const trackingSentDateLabel = trackingState?.lastContactAt
        ? formatDateFlexibleBR(trackingState.lastContactAt)
        : null;
      const patPlanDeliveryStatus = alreadyNotifiedPlan ? null : getReminderDeliveryStatus({
        dueDate: plan.due_date,
        createdAt: (plan as { created_at?: string | null }).created_at ?? null,
        lastContactAt: null,
        rule: reminderRule ? {
          isActive: reminderRule.is_active,
          offsetUnit: reminderRule.offset_unit,
          offsetValue: reminderRule.offset_value,
          sendTime: reminderRule.send_time,
          timezone: reminderRule.timezone,
        } : null,
      });
      const patPlanReminderDateLabel = patPlanDeliveryStatus?.type === "scheduled" || patPlanDeliveryStatus?.type === "expired"
        ? formatDateTimeBR((patPlanDeliveryStatus as { scheduledAt: Date }).scheduledAt.toISOString())
        : null;
      const patPlanTutorReminderStatus: FutureRow["tutorReminderStatus"] =
        alreadyNotifiedPlan ? "notified"
        : patPlanDeliveryStatus?.type === "expired" ? "window_passed"
        : patPlanReminderDateLabel ? "not_notified"
        : null;
      return {
        key: `plan-${plan.id}`,
        category: "vacina",
        typeLabel: "Vacina",
        detailLabel: plan.item?.name?.trim() || plan.item?.product_subtype?.name?.trim() || null,
        timestamp: resolveUpcomingSortTimestamp(plan.due_date),
        dateLabel: formatDateFlexibleBR(plan.due_date),
        appointmentDateLabel: isScheduled && plan.appointment?.starts_at ? formatDateTimeBR(plan.appointment.starts_at) : undefined,
        statusLabel: isScheduled ? (APPOINTMENT_STATUS_LABEL[plan.appointment?.status ?? ""] ?? "Agendado") : "Sem agendamento",
        statusKey: isScheduled ? plan.appointment?.status ?? null : null,
        isScheduled,
        appointmentId: isScheduled ? plan.appointment?.id ?? undefined : undefined,
        estimatedAmount: plan.item?.sale_price ?? null,
        resolutionStatus: isScheduled ? "scheduled" : "pending",
        petId: params.petId,
        ownerId: patientOwnerId,
        ownerName: patientOwnerName,
        professionalName: isScheduled ? getProfessionalName(upcomingAppointments.find((appointment) => appointment.id === plan.appointment?.id)) : null,
        tutorReminderStatus: patPlanTutorReminderStatus,
        tutorReminderDateLabel: trackingState?.lastContactAt ? trackingSentDateLabel : reminderState?.is_already_sent ? reminderSentDateLabel : patPlanReminderDateLabel,
        editablePlan: {
          id: plan.id,
          item_id: plan.item_id,
          due_date: plan.due_date,
          notes: plan.notes,
          appointment_id: plan.appointment_id,
          source_type: plan.source_type,
          source_pet_application_id: plan.source_pet_application_id,
          appointment: plan.appointment ?? null,
        },
        editableVaccination: null,
      };
    });

    const vaccineRows: FutureRow[] = (shouldSuppressPatientAutomations ? [] : classifiedVaccinationsWithPrice)
      .filter((vacc) => {
        if (plannedSourceVaccinationIds.has(vacc.vaccinationId)) return false;
        return !plannedKeys.has(`${vacc.itemId ?? ""}:${vacc.nextDoseRaw.slice(0, 10)}`);
      })
      .map((vacc) => {
        const isScheduled = vacc.resolutionStatus === "scheduled";
        const appointmentStatus = vacc.appointment?.status ?? "";
        const dueDateKey = vacc.nextDoseRaw.slice(0, 10);
        const trackingState = followUpTrackingMap?.get(`app:${vacc.vaccinationId}:${dueDateKey}`);
        const reminderKey = `${vacc.vaccinationId}:${dueDateKey}`;
        const reminderState = doseStateMap?.get(reminderKey);
        const matchedAppointment = vacc.appointment?.id
          ? upcomingAppointments.find((appointment) => appointment.id === vacc.appointment?.id)
          : undefined;
        const alreadyNotifiedPatVacc = Boolean(trackingState?.lastContactAt || reminderState?.is_already_sent);
        const reminderSentDateLabel = reminderState?.is_already_sent
          ? formatDateFlexibleBR(reminderState.latest_created_at)
          : null;
        const trackingSentDateLabel = trackingState?.lastContactAt
          ? formatDateFlexibleBR(trackingState.lastContactAt)
          : null;
        const patVaccDeliveryStatus = alreadyNotifiedPatVacc ? null : getReminderDeliveryStatus({
          dueDate: vacc.nextDoseRaw,
          createdAt: ((vaccinations as PetVaccinationUpcoming[]).find((item) => item.id === vacc.vaccinationId) as { created_at?: string | null } | undefined)?.created_at ?? null,
          lastContactAt: null,
          rule: reminderRule ? {
            isActive: reminderRule.is_active,
            offsetUnit: reminderRule.offset_unit,
            offsetValue: reminderRule.offset_value,
            sendTime: reminderRule.send_time,
            timezone: reminderRule.timezone,
          } : null,
        });
        const patVaccReminderDateLabel = patVaccDeliveryStatus?.type === "scheduled" || patVaccDeliveryStatus?.type === "expired"
          ? formatDateTimeBR((patVaccDeliveryStatus as { scheduledAt: Date }).scheduledAt.toISOString())
          : null;
        const patVaccTutorReminderStatus: FutureRow["tutorReminderStatus"] =
          alreadyNotifiedPatVacc ? "notified"
          : patVaccDeliveryStatus?.type === "expired" ? "window_passed"
          : patVaccReminderDateLabel ? "not_notified"
          : null;
        return {
          key: `vaccine-${vacc.vaccinationId}`,
          category: "vacina",
          typeLabel: "Vacina",
          detailLabel: vacc.vaccineName,
          timestamp: resolveUpcomingSortTimestamp(vacc.nextDoseRaw),
          dateLabel: vacc.isDateOnly ? formatDateOnlyBR(vacc.nextDoseRaw) : formatDateTimeBR(vacc.nextDoseRaw),
          appointmentDateLabel: isScheduled && vacc.appointment?.starts_at ? formatDateTimeBR(vacc.appointment.starts_at) : undefined,
          statusLabel: isScheduled ? (APPOINTMENT_STATUS_LABEL[appointmentStatus] ?? "Agendado") : "Sem agendamento",
          statusKey: isScheduled ? appointmentStatus : null,
          isScheduled,
          appointmentId: isScheduled ? vacc.appointment?.id ?? undefined : undefined,
          estimatedAmount: vacc.appointment?.id
            ? vaccineAppointmentPool.find((appointment) => appointment.id === vacc.appointment?.id)?.estimatedAmount ?? vacc.estimatedAmount ?? null
            : vacc.estimatedAmount ?? null,
          resolutionStatus: vacc.resolutionStatus,
          petId: params.petId,
          ownerId: patientOwnerId,
          ownerName: patientOwnerName,
          professionalName: getProfessionalName(matchedAppointment),
          tutorReminderStatus: patVaccTutorReminderStatus,
          tutorReminderDateLabel: trackingState?.lastContactAt ? trackingSentDateLabel : reminderState?.is_already_sent ? reminderSentDateLabel : patVaccReminderDateLabel,
          editablePlan: null,
          editableVaccination: (vaccinations as PetVaccinationUpcoming[]).find((item) => item.id === vacc.vaccinationId) as FutureRow["editableVaccination"],
        };
      });

    const returnRows: FutureRow[] = (shouldSuppressPatientAutomations ? [] : upcomingFollowUps)
      .map((item, index) => {
        const matchingAppointment = findMatchingReturnAppointmentRow({
          petId: params.petId,
          followUpDateKey: item.dateKey,
          appointmentRows,
        });

        return {
          key: `followup-${index}`,
          category: "retorno",
          typeLabel: "Retorno",
          timestamp: item.timestamp,
          dateLabel: item.label,
          appointmentDateLabel: matchingAppointment?.appointmentDateLabel,
          statusLabel: matchingAppointment?.statusLabel ?? "Sem agendamento",
          statusKey: matchingAppointment?.statusKey ?? null,
          isScheduled: Boolean(matchingAppointment?.appointmentId),
          appointmentId: matchingAppointment?.appointmentId,
          estimatedAmount: null,
          resolutionStatus: matchingAppointment?.appointmentId ? "scheduled" : "pending",
          petId: params.petId,
          ownerId: patientOwnerId,
          ownerName: patientOwnerName,
          professionalName: matchingAppointment?.professionalName ?? null,
          tutorReminderStatus: null,
          tutorReminderDateLabel: null,
          editablePlan: null,
          editableVaccination: null,
        };
      })
      .filter(Boolean) as FutureRow[];

    const combinedRows = [
      ...planRows,
      ...vaccineRows,
      ...returnRows,
      ...stayFutureRows,
      ...(
        includeLinkedAppointmentRows
          ? appointmentRows
          : (() => {
            const scheduledAppointmentIdsFromVaccines = new Set(vaccineRows.flatMap((row) => (row.appointmentId ? [row.appointmentId] : [])));
            const scheduledAppointmentIdsFromPlans = new Set(planRows.flatMap((row) => (row.appointmentId ? [row.appointmentId] : [])));
            const scheduledAppointmentIdsFromReturns = new Set(returnRows.flatMap((row) => (row.appointmentId ? [row.appointmentId] : [])));

            return appointmentRows.filter((row) => {
              if (!row.appointmentId) return true;
              return !scheduledAppointmentIdsFromVaccines.has(row.appointmentId)
                && !scheduledAppointmentIdsFromPlans.has(row.appointmentId)
                && !scheduledAppointmentIdsFromReturns.has(row.appointmentId);
            });
          })()
      ),
    ].sort((a, b) => a.timestamp - b.timestamp);

    return combinedRows;
  }, [
    applicationPlans,
    classifiedVaccinationsWithPrice,
    doseStateMap,
    followUpTrackingMap,
    getReminderDateLabel,
    includeLinkedAppointmentRows,
    ownerMedicalQueries,
    ownerPetIdsForQueries,
    ownerPlanQueries,
    ownerVaccinationQueries,
    params,
    petIds,
    primaryPet?.owner?.id,
    reminderRule,
    primaryPet?.owner?.name,
    shouldSuppressPatientAutomations,
    stayFutureRows,
    upcomingAppointments,
    upcomingFollowUps,
    vaccineAppointmentPool,
    vaccinations,
  ]);

  const isLoadingOwnerPetData =
    params.scope === "owner" && ownerPetIdsForQueries.length > 0
      ? ownerVaccinationQueries.some((q) => q.isLoading)
      || ownerPlanQueries.some((q) => q.isLoading)
      || ownerMedicalQueries.some((q) => q.isLoading)
      : false;

  const sections = useMemo<CareSections>(() => {
    const todayKey = toISODateOnlyLocal(now);
    const pendingRows = rows.filter((row) => getExpectedDateKeyForFutureRow(row) < todayKey);
    const upcomingRows = rows.filter((row) => getExpectedDateKeyForFutureRow(row) >= todayKey);

    return {
      pendingRows,
      upcomingRows,
    };
  }, [now, rows]);

  return {
    rows,
    ...sections,
    isLoading: params.scope === "patient"
      ? isLoadingPet || isLoadingVaccinations || isLoadingApplicationPlans || isLoadingMedicalRecords || (Boolean(params.clinicId) && isLoadingAppointments)
      : isLoadingOwnerPetData || (Boolean(params.clinicId) && isLoadingAppointments),
  };
}
